import type { FC, ReactNode } from 'react'
import { UserAvatar } from '@/components/ui/user-avatar'
import { cn } from '@/lib/utils'
import {
  AtIcon,
  BellIcon,
  ChatIcon,
  CheckCircleIcon,
  ClockCountdownIcon,
  ListChecksIcon,
  RobotIcon,
  WarningCircleIcon,
  XIcon,
} from '@phosphor-icons/react'
import { formatDistanceToNowStrict, isToday, isYesterday } from 'date-fns'

import type {
  Notification,
  NotificationFeedResponse,
  NotificationResolution,
} from '@scrambl/shared'
import { NotificationPriority, NotificationType } from '@scrambl/shared'

import '@/styles/notification-feed.css'

// ===== per-type presentation =====

const TIMESHEET_TYPES = new Set<NotificationType>([
  NotificationType.TIMESHEET_APPROVAL_DEADLINE,
  NotificationType.TIMESHEET_SUBMITTED,
  NotificationType.TIMESHEET_ENTRY_SUBMITTED,
  NotificationType.TIMESHEET_CLOSE_REMINDER,
  NotificationType.TIMESHEET_MISSING_REMINDER,
  NotificationType.TIMESHEET_APPROVED,
  NotificationType.TIMESHEET_REJECTED,
  NotificationType.TIMESHEET_ENTRY_APPROVED,
  NotificationType.TIMESHEET_ENTRY_REJECTED,
])

function getActionIcon(type: NotificationType): ReactNode {
  if (TIMESHEET_TYPES.has(type)) return <ClockCountdownIcon weight="fill" />
  switch (type) {
    case NotificationType.TASK_OVERDUE:
    case NotificationType.TASK_DUE_SOON:
    case NotificationType.TASK_SLA_BREACHED:
      return <WarningCircleIcon weight="fill" />
    case NotificationType.AGENT_REVIEW_REQUESTED:
      return <RobotIcon weight="fill" />
    default:
      return <BellIcon weight="fill" />
  }
}

function getActionTone(notification: Notification): 'bad' | 'warn' | 'accent' {
  if (notification.priority === NotificationPriority.URGENT) return 'bad'
  if (notification.priority === NotificationPriority.HIGH) return 'warn'
  return 'accent'
}

function getActionCta(type: NotificationType): string {
  switch (type) {
    case NotificationType.TIMESHEET_APPROVAL_DEADLINE:
    case NotificationType.TIMESHEET_SUBMITTED:
    case NotificationType.TIMESHEET_ENTRY_SUBMITTED:
    case NotificationType.AGENT_REVIEW_REQUESTED:
      return 'Review'
    case NotificationType.TIMESHEET_CLOSE_REMINDER:
    case NotificationType.TIMESHEET_MISSING_REMINDER:
      return 'Submit'
    default:
      return 'Open'
  }
}

function getModuleGlyph(notification: Notification): ReactNode {
  if (TIMESHEET_TYPES.has(notification.type)) return <ClockCountdownIcon />
  switch (notification.type) {
    case NotificationType.TASK_MENTION:
    case NotificationType.COMMENT_MENTION:
    case NotificationType.NOTE_MENTION:
    case NotificationType.TICKET_MENTION:
      return <AtIcon />
    case NotificationType.COMMENT_REPLY:
    case NotificationType.TASK_COMMENT_ADDED:
    case NotificationType.TICKET_COMMENT_ADDED:
      return <ChatIcon />
    case NotificationType.TASK_ASSIGNED:
    case NotificationType.TASK_STATUS_CHANGED:
      return <ListChecksIcon />
    default:
      return <BellIcon />
  }
}

function getActorName(notification: Notification): string | null {
  const actor = notification.actor
  if (!actor) return null
  return (
    actor.displayName ||
    [actor.firstName, actor.lastName].filter(Boolean).join(' ') ||
    actor.email ||
    null
  )
}

function getResolution(
  notification: Notification,
): NotificationResolution | null {
  const resolution = (notification.data as Record<string, unknown> | null)
    ?.resolution as NotificationResolution | undefined
  return resolution?.outcome ? resolution : null
}

/** Prefer the authored excerpt over the verbose event message in mention rows. */
export function getNotificationQuote(
  notification: Notification,
): string | null {
  const data = (notification.data ?? {}) as Record<string, unknown>
  const snippetKeysByType: Partial<
    Record<NotificationType, Array<keyof typeof data>>
  > = {
    [NotificationType.TASK_MENTION]: ['descriptionSnippet'],
    [NotificationType.COMMENT_MENTION]: ['commentSnippet'],
    [NotificationType.TICKET_MENTION]: [
      'commentSnippet',
      'descriptionSnippet',
      'ticketDescription',
    ],
    [NotificationType.NOTE_MENTION]: ['contentSnippet'],
    [NotificationType.COMMENT_REPLY]: ['commentSnippet'],
  }

  const keys = snippetKeysByType[notification.type] ?? []
  for (const key of keys) {
    const value = data[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }

  return notification.message?.trim() || null
}

function timeAgo(date: Date | string): string {
  return formatDistanceToNowStrict(new Date(date), { addSuffix: true })
}

function getDayBucket(date: Date | string): string {
  const d = new Date(date)
  if (isToday(d)) return 'Today'
  if (isYesterday(d)) return 'Yesterday'
  const weekAgo = new Date()
  weekAgo.setDate(weekAgo.getDate() - 7)
  return d >= weekAgo ? 'This week' : 'Earlier'
}

// ===== rows =====

/** Rows are <button>s, so the dismiss control lives in a wrapper, not nested. */
function DismissableItem({
  children,
  onDismiss,
}: {
  children: ReactNode
  onDismiss?: () => void
}) {
  if (!onDismiss) return <>{children}</>
  return (
    <div className="nfd-item-wrap">
      {children}
      <button
        type="button"
        className="nfd-dismiss"
        title="Dismiss"
        aria-label="Dismiss notification"
        onClick={event => {
          event.stopPropagation()
          onDismiss()
        }}
      >
        <XIcon weight="bold" />
      </button>
    </div>
  )
}

function ActionStack({
  notification,
  onSelect,
}: {
  notification: Notification
  onSelect: (n: Notification) => void
}) {
  const mergedCount = notification.unreadMessageCount ?? 1
  const lastAt =
    notification.lastMessageAt ??
    notification.updatedAt ??
    notification.createdAt
  const subParts = [notification.message, `updated ${timeAgo(lastAt)}`].filter(
    Boolean,
  )
  if (mergedCount > 1) {
    subParts.splice(1, 0, `${mergedCount} reminders merged`)
  }

  return (
    <button
      type="button"
      className="nfd-stack"
      onClick={() => onSelect(notification)}
    >
      <span
        className={cn(
          'nfd-stack-ico',
          `nfd-stack-ico-${getActionTone(notification)}`,
        )}
      >
        {getActionIcon(notification.type)}
      </span>
      <span className="nfd-stack-body">
        <span className="nfd-stack-title">{notification.title}</span>
        <span className="nfd-stack-sub">{subParts.join(' · ')}</span>
      </span>
      <span className="nfd-stack-cta">{getActionCta(notification.type)}</span>
    </button>
  )
}

function FeedRow({
  notification,
  onSelect,
  showQuote,
}: {
  notification: Notification
  onSelect: (n: Notification) => void
  showQuote?: boolean
}) {
  const actorName = getActorName(notification)
  const resolution = getResolution(notification)
  const isUnread = notification.status === 'UNREAD' && !notification.dismissedAt
  const mergedCount = notification.unreadMessageCount ?? 1
  const quote = showQuote ? getNotificationQuote(notification) : null

  return (
    <button
      type="button"
      className="nfd-row"
      onClick={() => onSelect(notification)}
    >
      <span className="nfd-row-avatar">
        {notification.actor ? (
          <UserAvatar
            user={{
              id: notification.actor.id,
              displayName: actorName ?? undefined,
              email: notification.actor.email,
            }}
            size="sm"
          />
        ) : (
          <span className="nfd-stack-ico">
            {getActionIcon(notification.type)}
          </span>
        )}
        <span className="nfd-row-module">{getModuleGlyph(notification)}</span>
      </span>

      <span className="nfd-row-body">
        <span
          className={cn('nfd-row-line', resolution && 'nfd-row-line-muted')}
        >
          <span className={cn(resolution && 'nfd-row-resolved')}>
            {notification.title}
          </span>
          {resolution && (
            <>
              {' — '}
              <span className="nfd-row-outcome">
                {resolution.outcome.toLowerCase()}
              </span>
            </>
          )}
        </span>
        {quote && !resolution && <span className="nfd-row-quote">{quote}</span>}
        <span className="nfd-row-meta">
          {resolution
            ? `auto-resolved ${timeAgo(resolution.resolvedAt)}`
            : timeAgo(notification.createdAt)}
        </span>
      </span>

      {mergedCount > 1 && <span className="nfd-count-pill">{mergedCount}</span>}
      {isUnread && !resolution && <span className="nfd-row-unread" />}
    </button>
  )
}

// ===== the panel =====

export type NotificationFeedLane = 'action' | 'mention'

export interface NotificationFeedPanelProps {
  feed: NotificationFeedResponse
  onSelect: (notification: Notification) => void
  /** Shows a hover ✕ on each row that dismisses the notification */
  onDismiss?: (notification: Notification) => void
  /** Shows "Dismiss all" on lane headers; dismisses the whole lane server-side */
  onDismissLane?: (lane: NotificationFeedLane) => void
  /** Cap items per lane (dropdown uses a smaller cap than the page) */
  maxPerLane?: number
  /** Render day rules inside the activity lane (page view) */
  showDayRules?: boolean
  className?: string
}

/**
 * Lane-based notification feed: action stacks with CTAs first, mentions with
 * quoted context second, everything else (incl. recently resolved) last.
 * Shared by the bell dropdown and the notifications page.
 */
export const NotificationFeedPanel: FC<NotificationFeedPanelProps> = ({
  feed,
  onSelect,
  onDismiss,
  onDismissLane,
  maxPerLane = 5,
  showDayRules = false,
  className,
}) => {
  const actionItems = feed.actionRequired.slice(0, maxPerLane)
  const mentionItems = feed.mentions.slice(0, maxPerLane)
  const activityItems = feed.activity.slice(0, Math.max(maxPerLane * 2, 10))

  const isEmpty =
    actionItems.length === 0 &&
    mentionItems.length === 0 &&
    activityItems.length === 0

  if (isEmpty) {
    return (
      <div className={cn('nfd-empty', className)}>
        <CheckCircleIcon className="mx-auto mb-2 h-8 w-8 opacity-40" />
        All caught up — nothing needs your attention
      </div>
    )
  }

  let lastDayBucket: string | null = null

  return (
    <div className={className}>
      {actionItems.length > 0 && (
        <>
          <div className="nfd-lane nfd-lane-action">
            Action required
            <span className="nfd-lane-count">{feed.counts.actionRequired}</span>
            {onDismissLane && (
              <button
                type="button"
                className="nfd-lane-action-btn"
                onClick={() => onDismissLane('action')}
                title="Dismiss every action item. Still-pending approvals return on the next daily reminder."
              >
                Dismiss all
              </button>
            )}
          </div>
          {actionItems.map(n => (
            <DismissableItem
              key={n.id}
              onDismiss={onDismiss ? () => onDismiss(n) : undefined}
            >
              <ActionStack notification={n} onSelect={onSelect} />
            </DismissableItem>
          ))}
        </>
      )}

      {mentionItems.length > 0 && (
        <>
          <div className="nfd-lane nfd-lane-mention">
            Mentions
            <span className="nfd-lane-count">{feed.counts.mentions}</span>
            {onDismissLane && (
              <button
                type="button"
                className="nfd-lane-action-btn"
                onClick={() => onDismissLane('mention')}
                title="Dismiss all mentions"
              >
                Dismiss all
              </button>
            )}
          </div>
          {mentionItems.map(n => (
            <DismissableItem
              key={n.id}
              onDismiss={onDismiss ? () => onDismiss(n) : undefined}
            >
              <FeedRow notification={n} onSelect={onSelect} showQuote />
            </DismissableItem>
          ))}
        </>
      )}

      {activityItems.length > 0 && (
        <>
          <div className="nfd-lane nfd-lane-activity">Everything else</div>
          {activityItems.map(n => {
            const bucket = getDayBucket(n.createdAt)
            const showRule = showDayRules && bucket !== lastDayBucket
            lastDayBucket = bucket
            return (
              <div key={n.id}>
                {showRule && <div className="nfd-day">{bucket}</div>}
                {/* Already-dismissed (resolved) rows have nothing to dismiss */}
                <DismissableItem
                  onDismiss={
                    onDismiss && !n.dismissedAt ? () => onDismiss(n) : undefined
                  }
                >
                  <FeedRow notification={n} onSelect={onSelect} />
                </DismissableItem>
              </div>
            )
          })}
        </>
      )}
    </div>
  )
}
